import { useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, Loader2, QrCode, RefreshCw, X, AlertCircle, Laptop } from 'lucide-react';
import jsQR from 'jsqr';
import { claimLoginPairingSession } from '../../security/login/loginPairingQrApi';
import { toast } from 'sonner';

interface SetupQrScannerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function SetupQrScannerModal({ open, onClose, onSuccess }: SetupQrScannerModalProps) {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const isClaimingRef = useRef<boolean>(false);

  // Iniciar câmera quando o modal abrir
  useEffect(() => {
    if (!open) {
      stopCamera();
      setIsSuccess(false);
      setIsAuthorizing(false);
      setErrorMsg(null);
      isClaimingRef.current = false;
      return;
    }

    startCamera();

    return () => {
      stopCamera();
    };
  }, [open]);

  const stopCamera = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  };

  const startCamera = async () => {
    stopCamera();
    setErrorMsg(null);
    setIsAuthorizing(false);
    setIsSuccess(false);
    isClaimingRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setHasPermission(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        await videoRef.current.play();
        setIsScanning(true);
        requestAnimationFrame(tick);
      }
    } catch (err: any) {
      console.error('Erro ao acessar câmera para leitura de QR Code:', err);
      setHasPermission(false);
      setErrorMsg(
        err.name === 'NotAllowedError'
          ? 'Permissão de acesso à câmera negada. Habilite a câmera nas configurações do navegador para escanear.'
          : 'Não foi possível inicializar a câmera do dispositivo.',
      );
    }
  };

  const extractSessionId = (text: string): string | null => {
    if (!text) return null;
    // Padrão 1: URL com /s/:sessionId
    const sMatch = text.match(/\/s\/([a-zA-Z0-9_-]+)/);
    if (sMatch && sMatch[1]) return sMatch[1];

    // Padrão 2: URL com ?s=:sessionId
    const qMatch = text.match(/[?&]s=([a-zA-Z0-9_-]+)/);
    if (qMatch && qMatch[1]) return qMatch[1];

    // Padrão 3: Token direto (UUID ou nanoid)
    if (/^[a-zA-Z0-9_-]{10,80}$/.test(text.trim())) {
      return text.trim();
    }

    return null;
  };

  const handleQrDetected = async (rawCode: string) => {
    if (isClaimingRef.current) return;
    const sessionId = extractSessionId(rawCode);

    if (!sessionId) {
      toast.error('Código QR não reconhecido como login do Aluno Conectado.');
      return;
    }

    isClaimingRef.current = true;
    setIsAuthorizing(true);
    setIsScanning(false);

    // Feedback tátil
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(100);
      } catch {}
    }

    // Parar stream
    stopCamera();

    try {
      const result = await claimLoginPairingSession(sessionId);
      if (result.ok) {
        setIsSuccess(true);
        toast.success('Computador autorizado com sucesso!');
        onSuccess?.();
      } else {
        setErrorMsg('Não foi possível autorizar o computador. O QR Code pode ter expirado.');
      }
    } catch (err: any) {
      console.error('Erro ao fazer claim da sessão QR:', err);
      const msg = err.response?.data?.message || 'QR Code expirado ou inválido. Atualize a tela de login no computador.';
      setErrorMsg(msg);
    } finally {
      setIsAuthorizing(false);
    }
  };

  const tick = () => {
    if (!videoRef.current || !canvasRef.current || isClaimingRef.current) return;

    if (videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      if (ctx) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });

        if (code && code.data) {
          handleQrDetected(code.data);
          return;
        }
      }
    }

    animFrameRef.current = requestAnimationFrame(tick);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
      <div className="relative w-full max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col text-white">
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white shadow-md">
              <QrCode size={18} />
            </div>
            <div>
              <h3 className="text-sm font-black text-white leading-tight">Escanear QR Code</h3>
              <p className="text-[11px] text-blue-200/70 font-medium">Aponte para a tela do computador</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Viewfinder Content */}
        <div className="relative w-full aspect-square bg-black flex items-center justify-center overflow-hidden">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Scanner Overlay Box */}
          {isScanning && !isAuthorizing && !isSuccess && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-8">
              <div className="relative w-56 h-56 border-2 border-dashed border-blue-400/60 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(59,130,246,0.3)]">
                <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />

                {/* Laser animation bar */}
                <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              </div>
            </div>
          )}

          {/* Estado: Autorizando */}
          {isAuthorizing && (
            <div className="absolute inset-0 bg-slate-900/90 flex flex-col items-center justify-center p-6 text-center gap-3">
              <Loader2 size={36} className="text-blue-400 animate-spin" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-white">Autorizando Computador...</p>
                <p className="text-xs text-slate-300">Conectando sua conta de Diretor ao navegador desktop</p>
              </div>
            </div>
          )}

          {/* Estado: Sucesso */}
          {isSuccess && (
            <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center p-6 text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shadow-lg animate-bounce">
                <CheckCircle2 size={32} />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-base font-black text-white">Computador Conectado!</p>
                <p className="text-xs text-slate-300 max-w-xs">
                  Sua sessão foi iniciada no computador. Você já pode continuar a implantação na tela grande.
                </p>
              </div>
            </div>
          )}

          {/* Estado: Erro / Permissão negada */}
          {errorMsg && !isAuthorizing && !isSuccess && (
            <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center p-6 text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center">
                <AlertCircle size={26} />
              </div>
              <p className="text-xs text-red-200 leading-relaxed max-w-xs">{errorMsg}</p>
              <button
                type="button"
                onClick={startCamera}
                className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-md"
              >
                <RefreshCw size={14} />
                <span>Tentar Novamente</span>
              </button>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-4 bg-slate-950/60 border-t border-white/10 flex flex-col gap-2">
          {isSuccess ? (
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs transition-colors shadow-md"
            >
              Concluir
            </button>
          ) : (
            <div className="flex items-center justify-between text-[11px] text-slate-400">
              <span className="flex items-center gap-1">
                <Laptop size={13} className="text-blue-400" />
                <span>Abra alunoconectado.com.br/login</span>
              </span>
              <button
                type="button"
                onClick={onClose}
                className="text-white/60 hover:text-white font-semibold transition-colors"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
